'use client';

import React, { type FC } from 'react';

import styles from './Table.module.css';

interface TableData {
  headers: {
    title: string;
    isHighlighted?: boolean;
    image?: { url: string; altText: string };
  }[];
  rows: {
    title?: string;
    values: (string | number | null)[];
  }[];
}

interface TableProps {
  tableData: TableData;
  className?: string;
  dataTestId?: string;
  clampLines?: number;
}

export const Table: FC<TableProps & { idPrefix: string }> = React.forwardRef<
  HTMLDivElement,
  TableProps & { idPrefix: string }
>(({ clampLines, className, dataTestId, tableData, ...restProps }, ref) => {
  const hasRowTitles = tableData.rows.some(row => row.title);

  return (
    <div
      className={`${styles.wrpr} ${className}`}
      data-test-id={dataTestId}
      ref={ref}
      {...restProps}
    >
      <table>
        <thead>
          <tr>
            {hasRowTitles && <th className={styles.rowTitle}></th>}
            {tableData.headers.map(header => (
              <th
                key={`${header.title}-${header.image?.url ?? 'noimg'}`}
                className={header.isHighlighted ? styles.highlighted : ''}
              >
                {header.image && <img src={header.image.url} alt={header.image.altText} />}
                {header.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, rowI) => (
            <tr key={row.title ?? row.values.join('|')}>
              {row.title && (
                <td className={styles.rowTitle}>
                  <span>{row.title}</span>
                </td>
              )}
              {row.values.map((value, i) => (
                <td key={`${rowI}-${tableData.headers[i].title}`}>
                  <div
                    className={clampLines ? styles.lineClamp : ''}
                    style={{ WebkitLineClamp: clampLines }}
                  >
                    {value}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

Table.displayName = 'Table';
